/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

//
// ───────────────────────────────────────────────── DEPENDENCIES AND COSTANT ─────
//
import { Injectable, inject } from "@angular/core";
import { Supabase } from "@app/@classes/supabase";
import { Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { Upload } from "tus-js-client";
import { AuthService } from "../shared/auth.service";
import { LoggerService } from "../shared/logger.service";

const IMAGE_URL_PREFIX = environment.supabase.imageRenderPrefix;
const FILE_URL_PREFIX = environment.supabase.fileUrlPrefix;

@Injectable({
	providedIn: "root",
})
export class UploaderService extends Supabase {
	public authService: AuthService = inject(AuthService);
	public loggerService: LoggerService = inject(LoggerService);

	public percentageSubject: Subject<number> = new Subject<number>();

	public async getImageData(
		file: File,
	): Promise<{ width: number; height: number; ratio: number }> {
		return new Promise((resolve, reject) => {
			const URL = window.URL || window.webkitURL;
			const img = new Image();
			const objectUrl = URL.createObjectURL(file);
			img.onload = () => {
				const w: number = img.width;
				const h: number = img.height;
				URL.revokeObjectURL(objectUrl);
				resolve({
					width: w,
					height: h,
					ratio: w / h,
				});
			};
			img.onerror = () => {
				reject();
			};
			img.src = objectUrl;
		});
	}

	private getResolutionFromRatio(
		maxDimension: number,
		ratio: number,
	): { width?: number; height?: number } {
		if (ratio > 1) {
			return {
				width: maxDimension,
				height: Math.round(maxDimension / ratio),
			};
		}
		return {
			width: Math.round(maxDimension * ratio),
			height: maxDimension,
		};
	}

	public async uploadFile(
		bucketName: string,
		path: string,
		fileName: string,
		file: File,
		progressSubject?: Subject<number>,
		options?: {
			sessionLess?: boolean;
			withoutPrefix?: boolean;
		},
	): Promise<string> {
		console.log("RECEIVED REQUEST FOR UPLOADING FILE", file);
		const {
			data: { session },
		} = await this.supabaseClient.auth.getSession();

		if (!session && !options?.sessionLess) {
			throw new Error("No session available");
		}

		this.loggerService.logInfo(
			"Uploading file to path",
			path,
			"into bucket",
			bucketName,
		);

		return new Promise<string>((resolve, reject) => {
			const upload = new Upload(file, {
				endpoint: environment.supabase.fileUrlResumablePrefix,
				retryDelays: [0, 3000, 5000, 10000, 20000],
				headers: {
					...(!session || options?.sessionLess
						? {}
						: {
							authorization: `Bearer ${session.access_token}`,
						}),
					"x-upsert": "true", // optionally set upsert to true to overwrite existing files
				},
				uploadDataDuringCreation: true,
				removeFingerprintOnSuccess: true, // Important if you want to allow re-uploading the same file https://github.com/tus/tus-js-client/blob/main/docs/api.md#removefingerprintonsuccess
				metadata: {
					bucketName: bucketName,
					objectName: path.length > 0 ? `${path}/${fileName}` : fileName,
					contentType: file.type,
					cacheControl: "3600",
				},
				chunkSize: 6 * 1024 * 1024, // NOTE: it must be set to 6MB (for now) do not change it -> supabase limit
				onError: (error) => {
					this.loggerService.logError(`Failed because: ${error}`);
					reject(error);
				},
				onProgress: (bytesUploaded, bytesTotal) => {
					const percentage = Math.ceil(
						(bytesUploaded / bytesTotal) * 100,
					).toFixed(2);

					if (progressSubject) {
						progressSubject.next(Number(percentage));
					}

					this.loggerService.logInfo(
						"Upload progress:",
						`${bytesUploaded} bytes / ${bytesTotal} bytes`,
						`${percentage}%`,
					);
				},
				onSuccess: () => {
					const isImage = file.type.startsWith("image");
					const prefix = isImage ? IMAGE_URL_PREFIX : FILE_URL_PREFIX;

					let downloadUrl = `${path.length > 0 ? `${path}/${fileName}` : fileName}`;
					if (!options?.withoutPrefix) {
						downloadUrl = `${prefix}${bucketName}/${downloadUrl}`;
					}

					// Make the downloadUrl valid replacing for example spaces with %20
					const encodedDownloadUrl = encodeURI(downloadUrl);

					this.loggerService.logInfo(
						`File uploaded. You can download ${fileName} from ${encodedDownloadUrl}`,
					);

					resolve(encodedDownloadUrl);
				},
			});

			// Check if there are any previous uploads to continue.
			return upload.findPreviousUploads().then((previousUploads) => {
				// Found previous uploads so we select the first one.
				if (previousUploads.length) {
					upload.resumeFromPreviousUpload(previousUploads[0]);
				}

				// Start the upload
				upload.start();
			});
		});
	}

	public deleteFile(bucketName: string, path: string): Promise<void> {
		const decodedPath = decodeURI(path);
		return this.supabaseClient.storage
			.from(bucketName)
			.remove([
				decodedPath
					.replace(IMAGE_URL_PREFIX, "")
					.replace(FILE_URL_PREFIX, "")
					.replace(`${bucketName}/`, ""),
			])
			.then(() => {
				this.loggerService.logInfo(`Deleted file ${path}`);
			})
			.catch((error) => {
				this.loggerService.logError(
					`Failed to delete file ${path} because: ${error}`,
				);
			});
	}
}
